import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ApiRequestError, getMe, getAgentStatus, type MeResponse, type AgentStatusResponse } from "@app/lib/api";
import { AppShell } from "@app/components/AppShell";
import Login from "@app/pages/Login";
import Register from "@app/pages/Register";
import Account from "@app/pages/Account";
import Dashboard from "@app/pages/Dashboard";
import Settings from "@app/pages/Settings";
import Chat from "@app/pages/Chat";

type AuthState =
  | { status: "loading" }
  | { status: "anon" }
  | { status: "authed"; me: MeResponse };

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });
  const [agent, setAgent] = useState<AgentStatusResponse | null>(null);
  const location = useLocation();

  useEffect(() => {
    let alive = true;
    getMe()
      .then((me) => alive && setAuth({ status: "authed", me }))
      .catch(() => alive && setAuth({ status: "anon" }));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (auth.status !== "authed") return;
    let alive = true;
    const tick = () => {
      getAgentStatus()
        .then((s) => alive && setAgent(s))
        .catch((e) => {
          if (e instanceof ApiRequestError && e.status === 404 && alive) {
            setAgent({ status: "none", lastPhoneHomeAt: null, serverType: null, region: null, publicIp: null });
          }
        });
    };
    tick();
    const id = window.setInterval(tick, 10_000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [auth.status]);

  if (auth.status === "loading") {
    return (
      <div className="flex min-h-full items-center justify-center text-sm text-muted-fg">
        Loading...
      </div>
    );
  }

  if (auth.status === "anon") {
    return (
      <Routes>
        <Route path="/login" element={<Login onAuthed={(me) => setAuth({ status: "authed", me })} />} />
        <Route path="/register" element={<Register onAuthed={(me) => setAuth({ status: "authed", me })} />} />
        <Route
          path="*"
          element={<Navigate to="/login" replace state={{ from: location.pathname }} />}
        />
      </Routes>
    );
  }

  const tone =
    agent?.status === "running"
      ? "ok"
      : agent?.status === "creating" ||
          agent?.status === "awaiting_phone_home" ||
          agent?.status === "provision_requested"
        ? "warn"
        : agent?.status === "error"
          ? "down"
          : "idle";
  const label =
    agent?.status === "running"
      ? "Running"
      : agent?.status === "creating"
        ? "Creating"
        : agent?.status === "awaiting_phone_home"
          ? "Booting"
          : agent?.status === "stopped"
            ? "Stopped"
            : agent?.status === "error"
              ? "Error"
              : "Idle";

  return (
    <AppShell
      me={auth.me}
      statusTone={tone}
      statusLabel={label}
      onLogout={() => setAuth({ status: "anon" })}
    >
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/chat" element={<Chat agent={agent} />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/account" element={<Account me={auth.me} />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/register" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
